import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAnalytics } from '~/analytics';
import type { UserRole } from '~/data/schemas/user';

import { CommandPalette } from './CommandPalette';

export function useCommandPalette({ role }: { role: UserRole }) {
  const enabled = role === 'consultant';
  const [open, setOpen] = useState(false);
  const { track } = useAnalytics();

  const openCommandPalette = useCallback(() => {
    if (!enabled) {
      return;
    }
    setOpen(true);
    track('command_palette_opened', { trigger: 'menu' });
  }, [enabled, track]);

  const closeCommandPalette = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        // Track outside the setOpen updater — React calls updater
        // callbacks twice in StrictMode dev to surface impurity, which
        // would double-fire the analytics event. Same pattern as
        // PropertyPortfolioTabContainer.onTabChange.
        if (!open) {
          track('command_palette_opened', { trigger: 'keyboard' });
        }
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, track, open]);

  return useMemo(
    () => ({
      openCommandPalette,
      element: enabled && open ? <CommandPalette onClose={closeCommandPalette} /> : null,
    }),
    [openCommandPalette, enabled, open, closeCommandPalette],
  );
}
